/**
 * useSearchFilters — manages filter state, URL synchronization, and filter handlers.
 *
 * Extracted from the old SearchPageComponent class methods:
 * resetAll, getHandleChangedValueFn, handleSortBy, toggleFilters.
 *
 * All URL-driven: filters live in the URL, this hook reads/writes them.
 */

import { useState, useCallback, useEffect } from 'react';
import omit from 'lodash/omit';

import { isMainSearchTypeKeywords } from '../../../util/search';
import { createResourceLocatorString } from '../../../util/routes';

import {
  validUrlQueryParamsFromProps,
  initialValues as computeInitialValues,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
  cleanSearchFromConflictingParams,
} from '../SearchPage.shared';

/**
 * @param {Object} deps
 * @param {Object} deps.config — marketplace configuration
 * @param {Object} deps.routeConfiguration — route config from context
 * @param {Object} deps.history — react-router history
 * @param {Object} deps.location — react-router location (may be enhanced for category pages)
 * @param {Object} deps.params — react-router path params
 * @param {Object} deps.searchParams — Redux searchParams from SearchPage duck
 */
export default function useSearchFilters({
  config,
  routeConfiguration,
  history,
  location,
  params = {},
  searchParams,
}) {
  // ── State ──────────────────────────────────────────────────────────
  const [currentQueryParams, setCurrentQueryParams] = useState(() => {
    return validUrlQueryParamsFromProps({ location, config, params });
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);

  // ── Sync state when URL changes (fixes stale state on category navigation) ──
  useEffect(() => {
    setCurrentQueryParams(validUrlQueryParamsFromProps({ location, config, params }));
  }, [location.pathname, location.search]);

  // ── Helpers ────────────────────────────────────────────────────────
  const propsLike = { location, config, params, searchParams };

  const urlQueryParams = validUrlQueryParamsFromProps(propsLike);

  const toggleFilters = useCallback(() => {
    setFiltersOpen(prev => !prev);
  }, []);

  const onOpenMobileModal = useCallback(() => setIsMobileModalOpen(true), []);
  const onCloseMobileModal = useCallback(() => setIsMobileModalOpen(false), []);

  // ── resetAll ───────────────────────────────────────────────────────
  const resetAll = useCallback(
    e => {
      setCurrentQueryParams({});
      history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, {}));
      if (e?.currentTarget) e.currentTarget.blur();
    },
    [routeConfiguration, history]
  );

  // ── getHandleChangedValueFn ────────────────────────────────────────
  const getHandleChangedValueFn = useCallback(
    useHistoryPush => {
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
      const activeListingTypes = config?.listing?.listingTypes.map(c => c.listingType);
      const listingCategories = config.categoryConfiguration.categories;
      const currentPathParams = params;
      const filterConfigs = {
        listingFieldsConfig,
        defaultFiltersConfig,
        listingCategories,
        activeListingTypes,
        currentPathParams,
      };

      const currentUrlQueryParams = validUrlQueryParamsFromProps(propsLike);

      return updatedURLParams => {
        setCurrentQueryParams(prevQueryParams => {
          const { address, bounds, keywords } = currentUrlQueryParams;
          const mergedQueryParams = { ...currentUrlQueryParams, ...prevQueryParams };

          const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
          const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);

          const next = omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              address,
              bounds,
            },
            filterConfigs
          );

          // Push to URL after state update
          if (useHistoryPush) {
            const search = cleanSearchFromConflictingParams(next, filterConfigs, sortConfig);
            const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
              routeConfiguration,
              location
            );
            // Use setTimeout to push after state settles
            setTimeout(() => {
              history.push(
                createResourceLocatorString(routeName, routeConfiguration, pathParams, search)
              );
            }, 0);
          }

          return next;
        });
      };
    },
    [config, location, routeConfiguration, history, params]
  );

  // ── handleSortBy ───────────────────────────────────────────────────
  const handleSortBy = useCallback(
    (urlParam, values) => {
      const currentUrlQueryParams = validUrlQueryParamsFromProps(propsLike);
      const queryParams = values
        ? { ...currentUrlQueryParams, [urlParam]: values }
        : omit(currentUrlQueryParams, urlParam);

      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
        routeConfiguration,
        location
      );

      history.push(
        createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
      );
    },
    [config, location, routeConfiguration, history, params]
  );

  // ── initialValues (for filter components) ──────────────────────────
  const getInitialValues = useCallback(() => {
    return computeInitialValues(propsLike, currentQueryParams);
  }, [location, config, params, currentQueryParams]);

  return {
    currentQueryParams,
    urlQueryParams,
    filtersOpen,
    isMobileModalOpen,
    toggleFilters,
    onOpenMobileModal,
    onCloseMobileModal,
    resetAll,
    getHandleChangedValueFn,
    handleSortBy,
    getInitialValues,
  };
}
